/**
 * Videos Page (Vídeo-aulas)
 *
 * Lista as vídeo-aulas publicadas pelo Admin. Cada card abre o player.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Video, Play, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useVideos } from '@/hooks/use-learning';
import { resolveMediaUrl } from '@/lib/api';
import { coverPositionStyle } from '@/lib/cover';
import type { Video as VideoType } from '@/types/learning';

function formatDuration(seconds?: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VideosPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const { data, isLoading } = useVideos(searchTerm, page);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleOpen = (videoId: string) => {
    navigate(`/learn/videos/${videoId}`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Video className="h-6 w-6 text-primary" />
            Vídeo-aulas
          </h1>
          <p className="text-muted-foreground mt-1">
            Aprenda sobre finanças pessoais com nossas vídeo-aulas
          </p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar vídeo-aulas..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => handleSearch('')}
                aria-label="Limpar busca de vídeo-aulas"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Grid */}
      {(() => {
        if (isLoading) return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (!data || data.videos.length === 0) return (
            <Card>
              <CardContent className="py-12 text-center">
                <Video className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhuma vídeo-aula encontrada</h3>
                <p className="text-muted-foreground">
                  {searchTerm ? 'Tente ajustar sua busca.' : 'Ainda não há vídeo-aulas disponíveis.'}
                </p>
              </CardContent>
            </Card>
        );
        return (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {data.videos.map((video: VideoType) => {
                const duration = formatDuration(video.duration_seconds);
                return (
                  <Card
                    key={video.id}
                    className="overflow-hidden hover:shadow-lg transition-shadow group cursor-pointer"
                    onClick={() => handleOpen(video.id)}
                  >
                    <div className="relative aspect-video bg-muted">
                      {video.thumbnail_url ? (
                        <img
                          src={resolveMediaUrl(video.thumbnail_url)}
                          style={coverPositionStyle(video.cover_position)}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                          <Video className="h-12 w-12 text-primary/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="rounded-full bg-white/90 p-4">
                          <Play className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                      {duration && (
                        <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {duration}
                        </span>
                      )}
                    </div>

                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg line-clamp-2">{video.title}</CardTitle>
                      {video.description && (
                        <CardDescription className="line-clamp-2">{video.description}</CardDescription>
                      )}
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
        );
      })()}

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Anterior
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Página {page} de {data.total_pages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
            disabled={page === data.total_pages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
